import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Eye, FileText, BellRing, Pencil, Plus, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORIES, ACCEPT, checkFile } from "./ProductForm";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useApi, errMsg, fmtDate, fmtBytes, fetchBlob, downloadFile } from "@/lib/api";
import { ProductIcon, Spinner, StatusBadge } from "@/components/ww/Misc";
import { CoverageTimeline } from "@/components/ww/CoverageTimeline";

const OFFSET = { 30: "30 days before", 7: "7 days before", 1: "1 day before", 0: "On expiry day" };

function Reminders({ items }) {
  return (
    <section className="mt-6 rounded-[26px] border border-ww-border bg-white p-6 sm:p-8" data-testid="shared-reminders">
      <h2 className="flex items-center gap-2 font-display text-xl font-semibold text-ww-navy"><BellRing className="h-5 w-5 text-ww-emerald" />Reminders</h2>
      {items.length === 0 ? <p className="mt-4 text-sm text-ww-muted">No reminders scheduled.</p> : (
        <div className="mt-4 divide-y divide-ww-border/70">
          {items.map((r) => (
            <div key={r.id} className="flex items-center justify-between py-3 text-sm" data-testid={`shared-reminder-${r.id}`}>
              <span className="text-ww-navy">{r.coverage_name || (r.coverage_type === "warranty" ? "Warranty" : "Guarantee")} · {OFFSET[r.offset_days] || "Reminder"}</span>
              <span className={`text-xs font-semibold ${r.status === "sent" ? "text-ww-muted" : "text-ww-emerald"}`}>{r.status === "sent" ? "Sent" : fmtDate(r.due_date)}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function SharedProduct() {
  const { ownerId, id } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [cat, setCat] = useState("invoice");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const base = `/family/shared/${ownerId}`;
  const load = useCallback(() => api("get", `${base}/products/${id}`).then(setP).catch((e) => { toast.error(errMsg(e), { id: "shared-product-error" }); navigate(`/shared/${ownerId}`); }), [api, base, id, ownerId, navigate]);
  useEffect(() => { load(); }, [load]);
  const upload = async (file) => {
    const err = checkFile(file);
    if (err) return toast.error(err);
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", cat);
    try { await api("post", `${base}/products/${id}/documents`, fd); toast.success("Document saved securely"); await load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  useEffect(() => () => { if (viewer) { URL.revokeObjectURL(viewer.url); } }, [viewer]);
  const view = async (d) => {
    try { setViewer({ url: await fetchBlob(api, `${base}/documents/${d.id}/file`), type: d.content_type, name: d.filename }); }
    catch (e) { toast.error("Could not open document"); }
  };
  if (!p) return <Spinner />;
  const fields = [["Brand", p.brand], ["Model", p.model_number], ["Serial Number", p.serial_number], ["Purchase Date", fmtDate(p.purchase_date)],
    ["Purchase Price", p.purchase_price != null ? new Intl.NumberFormat(undefined, { style: "currency", currency: p.price_currency || "INR" }).format(p.purchase_price) : null], ["Seller", p.seller]];
  return (
    <main className="mx-auto max-w-5xl px-5 py-12 lg:px-8" data-testid="shared-product-page">
      <Link to={`/shared/${ownerId}`} className="flex items-center gap-1.5 text-sm font-semibold text-ww-muted hover:text-ww-emerald" data-testid="shared-product-back"><ArrowLeft className="h-4 w-4" />{p.owner_name}'s Wallet</Link>
      <div className="mt-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
      <div className="flex items-center gap-4">
        <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-ww-mint text-ww-emerald"><ProductIcon name={p.name} className="h-8 w-8" /></span>
        <div><h1 className="font-display text-3xl font-semibold tracking-tight text-ww-navy sm:text-4xl" data-testid="shared-product-name">{p.name}</h1>
          <div className="mt-2 flex items-center gap-2"><StatusBadge status={p.status} testid="shared-product-status" /><span className="text-xs font-semibold uppercase text-ww-muted">{p.can_edit ? "Can add & edit" : "View only"}</span></div></div>
      </div>
      {p.can_edit && <Link to={`/shared/${ownerId}/products/${id}/edit`} className="ww-btn-ghost w-fit" data-testid="shared-edit-product-button"><Pencil className="h-4 w-4" />Edit Product</Link>}
      </div>
      <section className="mt-8 grid gap-6 rounded-[26px] border border-ww-border bg-white p-6 sm:grid-cols-3 sm:p-8">
        {fields.map(([l, v]) => <div key={l}><p className="text-xs font-medium uppercase tracking-wider text-ww-muted">{l}</p><p className="mt-1 break-words font-semibold text-ww-navy">{v || "—"}</p></div>)}
      </section>
      <CoverageTimeline coverages={p.coverages} />
      <Reminders items={p.reminders} />
      <section className="mt-6 rounded-[26px] border border-ww-border bg-white p-6 sm:p-8" data-testid="shared-documents">
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <h2 className="font-display text-xl font-semibold text-ww-navy">Documents</h2>
          {p.can_edit && p.documents.length < 10 && (
            <div className="flex gap-2">
              <Select value={cat} onValueChange={setCat}><SelectTrigger className="h-10 w-48 rounded-xl" data-testid="shared-add-document-category"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
              <button className="ww-btn-primary" disabled={busy} onClick={() => fileRef.current.click()} data-testid="shared-add-document-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Add</button>
            </div>
          )}
        </div>
        <input ref={fileRef} type="file" accept={ACCEPT} hidden onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; if (f) upload(f); }} data-testid="shared-add-document-input" />
        <div className="mt-4 space-y-3">
          {p.documents.length === 0 && <p className="text-sm text-ww-muted">No documents uploaded.</p>}
          {p.documents.map((d) => (
            <div key={d.id} className="flex flex-col gap-3 rounded-2xl border border-ww-border p-4 sm:flex-row sm:items-center" data-testid={`shared-document-${d.id}`}>
              <div className="flex min-w-0 flex-1 items-center gap-3"><FileText className="h-5 w-5 shrink-0 text-ww-emerald" /><div className="min-w-0"><p className="truncate text-sm font-semibold text-ww-navy">{d.filename}</p><p className="text-xs text-ww-muted">{fmtBytes(d.size)}</p></div></div>
              <div className="flex gap-1.5">
                <button onClick={() => view(d)} className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold text-ww-navy hover:bg-ww-mint" data-testid={`shared-document-view-${d.id}`}><Eye className="h-3.5 w-3.5" />View</button>
                <button onClick={() => downloadFile(api, `${base}/documents/${d.id}/file?download=1`, d.filename).catch(() => toast.error("Download failed"))} className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold text-ww-navy hover:bg-ww-mint" data-testid={`shared-document-download-${d.id}`}><Download className="h-3.5 w-3.5" />Download</button>
              </div>
            </div>
          ))}
        </div>
      </section>
      <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent className="max-h-[94vh] max-w-4xl rounded-[24px] p-4" data-testid="shared-document-viewer">
          <DialogTitle className="truncate pr-8 text-base">{viewer?.name}</DialogTitle>
          <DialogDescription className="sr-only">Document preview</DialogDescription>
          {viewer?.type === "application/pdf" ? <iframe title="Document" src={viewer.url} className="h-[75vh] w-full rounded-xl" /> : viewer && <img src={viewer.url} alt={viewer.name} className="mx-auto max-h-[75vh] rounded-xl object-contain" />}
        </DialogContent>
      </Dialog>
    </main>
  );
}
